import { TypeOf, z } from "zod";
import { IToast } from "../components/toastCenter";
import { SettingPageType } from "./settings";
import { PageTypes } from "./navigation";
import { IUser } from "./user";
import { IHuntGroup, IPhonebookContact } from "./auth";
import { ICall } from "./call";

export type PhonePageType = 'history' | 'missed' | 'parked' | 'voicemail' | 'queue';

interface dragAndDropProps {
    id: string;
    extraData?: Record<string, any>
}

export interface IApp {
    active_page: PageTypes;
    active_phone_tab: PhonePageType;
    drag_and_drop: dragAndDropProps | null;
    grid_view: boolean;
    toasts: IToast[];
    settings_page: SettingPageType;
    provisioning_open: boolean;
    status_subscriptions: string[];
    focus_on_department?: string;
    global_settings: GlobalSettings;
    global_settings_updated?: boolean;
    viewContact?: ViewContactType;
    editPhonebookContact?: IPhonebookContact;
    smartDiallerError?: SmartDiallerError;
    apiOnlyPhonebookUuids: string[];
    has_devices: boolean;
    smart_dial: SmartDialProps;
    global_settings_got: boolean;
    chatType: 0 | 1 | 2;
}

export interface SmartDialProps {
    apps: SmartDiallerApp[];
    registrations: SmartDiallerRegistration[];
    location_ids: string[];
}

export interface SmartDiallerError extends ICall {
    reasonPhrase?: string;
    statusCode?: number;
}

export interface GlobalSettings {
    do_not_disturb: boolean;
    selected_phonebooks: Record<string, boolean>;
    favourite_contact: Record<string, boolean>;
    pinned_chats: Record<string, boolean>;
    muted_chats: Record<string, boolean>;
    mark_chat_unread: Record<string, boolean>;
    sync_dnd_to_phones: boolean;
    attended_transfer: boolean;
}

export type GlobalSettingsDictionaryName =
    'selected_phonebooks'
    | 'favourite_contact'
    | 'pinned_chats'
    | 'muted_chats'
    | 'mark_chat_unread';

export type ViewContactType = {
    type: 'user';
    data: IUser;
} | {
    type: 'hunt_group';
    data: IHuntGroup;
} | {
    type: 'phonebook_contact';
    data: IPhonebookContact;
} | {
    type: 'tenant';
    data: string;
}

export interface SmartDiallerRegistration {
    call_id: string;
    location_id: string;
    username: string;
    cc: string;
    registered_at: Date;
    expires: Date;
    received: string;
    contact: string;
    user_agent: string;
    nickName?: string;
}

export interface SmartDiallerApp {
    uuid: string;
    device_platform: number;
    app_version: number;
    device_name: string;
    device_model: string;
}

export const AuxCodeValidator = z.object({
    code: z.string(),
    set_by: z.string(),
    time: z.string(),
})

export const SetAuxCodeResponse = z.object({
    code: z.string(),
    set_by: z.string(),
    time: z.string(),
    user_uuid: z.string(),
});

export type AuxCode = TypeOf<typeof AuxCodeValidator>;

export const auraQueueLogin = z.object({
    login: z.boolean(),
    set_by: z.string().optional(),
    time: z.string().optional(),
    queue_id: z.string().optional(),
})

export type QueueLogin = TypeOf<typeof auraQueueLogin>;

export const queuePanelSchema = z.object({
    uuid: z.string(),
    queue_uuid: z.string(),
    alerts: z.object({
        max_wait_time: z.number().optional(),
        average_wait_time: z.number().optional(),
        max_call_waiting: z.number().optional(),
        min_agents_available: z.number().optional(),
        min_agents_online: z.number().optional(),
    }).optional(),
    queue_users: z.array(z.string()),
    name: z.string(),
    allow_login: z.boolean(),
    is_supervisor: z.boolean(),
});

export type QueuePanel = TypeOf<typeof queuePanelSchema>;

export const queueStatsResponse = z.object({
    in_queue: z.number(),
    in_queue_longest: z.number(),
    login_users: z.array(z.string()),
    stats: z.object({
        average_call_duration: z.number(),
        average_duration: z.number(),
        average_failed_times: z.number(),
        average_ring_duration: z.number(),
        average_wait_duration: z.number(),
        max_call_duration: z.number(),
        max_duration: z.number(),
        max_failed_times: z.number(),
        max_ring_duration: z.number(),
        max_wait_duration: z.number(),
        num_abandoned: z.number(),
        num_answered: z.number(),
        num_answered_within: z.number(),
        num_answered_within_all: z.number(),
        num_calls: z.number(),
        num_noregistered: z.number(),
        num_ongoing: z.number(),
        num_ringing: z.number(),
        num_shortcode: z.number(),
        num_timeout: z.number(),
        total_call_duration: z.number(),
        total_duration: z.number(),
        total_failed_times: z.number(),
        total_ring_duration: z.number(),
        total_wait_duration: z.number(),
    }),
    user_stats: z.record(
        z.object({
            average_call_duration: z.number(),
            average_duration: z.number(),
            average_failed_times: z.number(),
            average_ring_duration: z.number(),
            average_wait_duration: z.number(),
            last_call_answer: z.string().optional(),
            last_call_start: z.string().optional(),
            last_call_end: z.string().optional(),
            max_call_duration: z.number(),
            max_duration: z.number(),
            max_failed_times: z.number(),
            max_ring_duration: z.number(),
            max_wait_duration: z.number(),
            num_abandoned: z.number(),
            num_answered: z.number(),
            num_answered_within: z.number(),
            num_answered_within_all: z.number(),
            num_calls: z.number(),
            num_noregistered: z.number(),
            num_ongoing: z.number(),
            num_ringing: z.number(),
            num_shortcode: z.number(),
            num_timeout: z.number(),
            status: z.string(),
            total_call_duration: z.number(),
            total_duration: z.number(),
            total_failed_times: z.number(),
            total_ring_duration: z.number(),
            total_wait_duration: z.number(),
            user_uuid: z.string(),
        })
    ),
});

export type QueueStats = TypeOf<typeof queueStatsResponse>;

export interface QueueInfoItem {
    in_queue: number;
    in_queue_longest: number;
    stats: QueueStats['stats'];
    user_stats: QueueStats['user_stats'];
    agentsOnline: number;
    alerts: {
        agentsOnline: {
            alert: boolean;
            threshold: number;
        };
        agentsAvailable: {
            alert: boolean;
            threshold: number;
        };
        callsWaiting: {
            alert: boolean;
            threshold: number | null;
        };
        averageWait: {
            alert: boolean;
            threshold: number | null;
        };
        longestWait: {
            alert: boolean;
            threshold: number | null;
        };
    };
}

export interface QueueAlertChecker {
    queueInfo: Record<string, QueueInfoItem>
    isQueueAlert: boolean
    // agentsAvailable: number
}
